import { store } from '../store';
import { addTranscript } from '../store/conversationSlice';
import { config } from '../config';
import { LiveReconnectScheduler } from './liveReconnect';
import { MicrophoneUnavailableError, openMicrophoneStream } from './microphoneAccess';
import { validateAndRefreshToken } from '../utils/tokenUtils';
import { participantIdOf } from '../utils/participantId';
import { LinearResampler, PcmFrame, PcmFrameAccumulator } from '../utils/pcmFraming';
import { CaptureClock } from '../utils/captureClock';
import {
  CAPTURE_STAMP_INTERVAL_MS,
  CLOCK_SYNC_VERSION,
  COSELLER_CAPTURE_PROCESSOR,
  COSELLER_CHANNELS,
  COSELLER_ENCODING,
  COSELLER_FRAME_SAMPLES,
  COSELLER_MIC_FRAME,
  COSELLER_SAMPLE_RATE,
  CREATOR_PARTICIPANT_ID,
  PCM16_BYTES_PER_MS,
} from '../constants/coSellerMic';

/** Where a co-seller's mic streams: the call's room, as their roster participant. */
export interface CoSellerMicTarget {
  roomId: string;
  /** `salesperson_<n>` from the join. */
  participantId: string;
}

/** A running microphone capture: raw Float32 blocks at `sampleRate`. */
export interface MicCaptureHandle {
  readonly sampleRate: number;
  stop(): Promise<void>;
}

export type MicBlockListener = (samples: Float32Array) => void;

/**
 * Opens the microphone and calls `onBlock` with every captured block. Swapped
 * in tests; the browser's is `openBrowserMicCapture`.
 */
export type MicCaptureFactory = (deviceId: string | null, onBlock: MicBlockListener) => Promise<MicCaptureHandle>;

interface MicServerFrame {
  type?: string;
  clockSync?: number;
  id?: number;
  server_ms?: number;
  text?: string;
  speaker?: string;
  participantId?: string | number | null;
  speakerName?: string | null;
  is_final?: boolean;
  timestamp?: string;
  message?: string;
}

/** Client UTC in ms on ONE clock for both pongs and stamps (Date.now() may step; this does not). */
const browserClientNowMs = (): number => performance.timeOrigin + performance.now();

/** The worklet forwards every raw render quantum to the main thread; all framing happens there. */
const CAPTURE_WORKLET_SOURCE = `
class CoSellerMicCapture extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel && channel.length) this.port.postMessage(new Float32Array(channel));
    return true;
  }
}
registerProcessor('${COSELLER_CAPTURE_PROCESSOR}', CoSellerMicCapture);
`;

/**
 * The browser's capture: an AudioContext asked for 16 kHz (Chrome and Safari
 * honour it and resample the mic themselves), falling back to the device's
 * own rate where the mic cannot be connected to it; `echoCancellation` on.
 * The context is created FIRST, synchronously, so a call made from a click
 * creates it inside the user's gesture (Safari's autoplay rule).
 */
export const openBrowserMicCapture: MicCaptureFactory = async (deviceId, onBlock) => {
  let context = new AudioContext({ sampleRate: COSELLER_SAMPLE_RATE });
  let stream: MediaStream | null = null;
  try {
    stream = await openMicrophoneStream({
      echoCancellation: true,
      channelCount: COSELLER_CHANNELS,
      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
    });
    let source: MediaStreamAudioSourceNode;
    try {
      source = context.createMediaStreamSource(stream);
    } catch (rateError) {
      console.warn('[CoSellerMic] The mic cannot feed a 16 kHz context here; capturing at its own rate', rateError);
      await context.close();
      context = new AudioContext();
      source = context.createMediaStreamSource(stream);
    }
    if (context.state === 'suspended') await context.resume();

    const workletUrl = URL.createObjectURL(new Blob([CAPTURE_WORKLET_SOURCE], { type: 'application/javascript' }));
    try {
      await context.audioWorklet.addModule(workletUrl);
    } finally {
      URL.revokeObjectURL(workletUrl);
    }
    const captureNode = new AudioWorkletNode(context, COSELLER_CAPTURE_PROCESSOR);
    captureNode.port.onmessage = (event: MessageEvent<Float32Array>) => onBlock(event.data);
    source.connect(captureNode);
    // The graph must pull the capture node for it to run, but it must not be heard.
    const silentSink = context.createGain();
    silentSink.gain.value = 0;
    captureNode.connect(silentSink).connect(context.destination);

    const runningContext = context;
    const runningStream = stream;
    return {
      sampleRate: runningContext.sampleRate,
      stop: async () => {
        captureNode.port.close();
        captureNode.disconnect();
        runningStream.getTracks().forEach((track) => track.stop());
        if (runningContext.state !== 'closed') await runningContext.close();
      },
    };
  } catch (error) {
    stream?.getTracks().forEach((track) => track.stop());
    if (context.state !== 'closed') await context.close().catch(() => undefined);
    if (error instanceof MicrophoneUnavailableError) throw error;
    throw new MicrophoneUnavailableError('The microphone could not be started', error);
  }
};

/**
 * The C2 audio URL: the desktop apps' mic socket, with the co-seller's
 * participant and the clock protocol (`clock=1`).
 */
export const buildCoSellerMicUrl = (baseUrl: string, target: CoSellerMicTarget, token: string): string => {
  const query = new URLSearchParams({
    room_id: target.roomId,
    speaker: CREATOR_PARTICIPANT_ID,
    sample_rate: String(COSELLER_SAMPLE_RATE),
    encoding: COSELLER_ENCODING,
    channels: String(COSELLER_CHANNELS),
    token,
  });
  if (target.participantId !== CREATOR_PARTICIPANT_ID) query.set('participant_id', target.participantId);
  query.set('clock', String(CLOCK_SYNC_VERSION));
  return `${baseUrl}?${query.toString()}`;
};

/**
 * A web co-seller's own microphone on someone's desktop call (D6,
 * 2026-10-05), on exactly the desktop apps' wire (services C2):
 *  - 16 kHz mono `linear16`, 100 ms binary frames, sent only while the socket
 *    is up (audio captured while it is down is dropped, never buffered);
 *  - text frames ONLY after the welcome says `clockSync: 1` (an older proxy
 *    reads bytes only and would crash on text): `clock_pong` answers each
 *    `clock_ping` at once, and a `capture_stamp` precedes the first frame,
 *    follows every CAPTURE_STAMP_INTERVAL_MS of audio and any capture gap;
 *  - every frame goes as captured: the mic is on until the co-seller leaves,
 *    there is no mute (owner, 2026-10-07);
 *  - every reconnect is a new socket with a FRESH token: `audio_ms` restarts
 *    at 0 and a fresh stamp goes first. It reconnects under the shared live
 *    policy as `coSellerMic`, so a drop shows the reconnect overlay.
 * The proxy's `transcript` frames on this socket are the co-seller's own lines
 * (finals are shown); everyone else's arrive on `/ws/desktop`.
 *
 * `acquire()` opens the microphone first (from the Join click, so the browser
 * prompts and the AudioContext starts inside the gesture); `start()` connects
 * it to the call once the join succeeded; `stop()` releases everything.
 */
export class CoSellerMicService {
  private capture: MicCaptureHandle | null = null;
  private resampler: LinearResampler | null = null;
  private accumulator: PcmFrameAccumulator | null = null;
  private clock: CaptureClock | null = null;
  private target: CoSellerMicTarget | null = null;
  private ws: WebSocket | null = null;
  private isWelcomed = false;
  private isClockSync = false;
  private bytesSent = 0;
  /** No stamp yet on this socket: the first frame is due one. */
  private lastStampAudioMs: number | null = null;
  /** The capture stalled since the last stamp: the next frame is due one. */
  private hasCaptureGap = false;
  /** Bumped by every acquire/stop, so a capture that finishes opening after a stop is closed again. */
  private generation = 0;
  private readonly reconnect = new LiveReconnectScheduler('coSellerMic', () => this.open());

  constructor(
    private readonly openCapture: MicCaptureFactory = openBrowserMicCapture,
    private readonly clientNowMs: () => number = browserClientNowMs
  ) {}

  /** Open the microphone. Rejects with MicrophoneUnavailableError. Call it straight from the click. */
  async acquire(deviceId: string | null): Promise<void> {
    this.stop();
    const generation = this.generation;
    const capture = await this.openCapture(deviceId, (samples) => {
      if (generation === this.generation) this.handleBlock(samples);
    });
    if (generation !== this.generation) {
      await capture.stop();
      return;
    }
    this.capture = capture;
    this.resampler = new LinearResampler(capture.sampleRate, COSELLER_SAMPLE_RATE);
    this.accumulator = new PcmFrameAccumulator(COSELLER_FRAME_SAMPLES);
    this.clock = new CaptureClock(COSELLER_SAMPLE_RATE);
  }

  /** Stream the acquired microphone into the call. */
  async start(target: CoSellerMicTarget): Promise<void> {
    if (!this.capture) throw new Error('The co-seller mic must be acquired before it can stream');
    this.target = target;
    this.reconnect.activate();
    await this.open();
  }

  isActive(): boolean {
    return this.capture !== null;
  }

  /** Leave, end or sign-out: close the socket, stop retrying, release the microphone. */
  stop(): void {
    this.generation += 1;
    this.reconnect.stop();
    this.closeSocket();
    this.target = null;
    const capture = this.capture;
    this.capture = null;
    this.resampler = null;
    this.accumulator = null;
    this.clock = null;
    capture?.stop().catch((error) => console.warn('[CoSellerMic] Releasing the microphone failed:', error));
  }

  private async open(): Promise<void> {
    const target = this.target;
    if (!target) throw new Error('The co-seller mic has no call to stream to');
    const { token } = await validateAndRefreshToken();

    // stop() ran, or another call replaced this one, during the token fetch.
    if (!this.reconnect.isActive() || this.target !== target) return;
    if (!token) {
      // No socket was opened, so no close event will retry: retry from here.
      this.reconnect.scheduleReconnect();
      throw new Error('No Firebase token for the co-seller mic socket');
    }

    this.closeSocket();
    const socket = new WebSocket(buildCoSellerMicUrl(config.server.deepgramUrl, target, token));
    socket.binaryType = 'arraybuffer';
    this.ws = socket;
    this.resetSocketState();

    socket.onopen = () => {
      if (this.ws !== socket) return;
      this.reconnect.markConnected();
      console.info(`[CoSellerMic] Streaming into room ${target.roomId} as ${target.participantId}`);
    };
    socket.onmessage = (event: MessageEvent) => {
      if (this.ws !== socket || typeof event.data !== 'string') return;
      this.handleServerFrame(event.data, socket, target);
    };
    socket.onerror = (event) => console.warn('[CoSellerMic] Socket error:', event);
    socket.onclose = (event) => {
      // A socket this service replaced or closed on purpose reports nothing.
      if (this.ws !== socket) return;
      this.ws = null;
      this.resetSocketState();
      console.info(`[CoSellerMic] Socket closed: ${event?.code} ${event?.reason ?? ''}`);
      this.reconnect.scheduleReconnect();
    };
  }

  /** A new socket is a new stream: `audio_ms` restarts at 0 and the first frame is stamped. */
  private resetSocketState(): void {
    this.isWelcomed = false;
    this.isClockSync = false;
    this.bytesSent = 0;
    this.lastStampAudioMs = null;
  }

  private handleServerFrame(rawFrame: string, socket: WebSocket, target: CoSellerMicTarget): void {
    let frame: MicServerFrame;
    try {
      frame = JSON.parse(rawFrame);
    } catch (error) {
      console.warn('[CoSellerMic] Unreadable frame:', error);
      return;
    }

    switch (frame?.type) {
      case COSELLER_MIC_FRAME.welcome:
        this.isWelcomed = true;
        this.isClockSync = frame.clockSync === CLOCK_SYNC_VERSION;
        return;
      case COSELLER_MIC_FRAME.clockPing:
        // An older proxy never pings; one that does but never said clockSync gets no text.
        if (!this.isClockSync) return;
        socket.send(
          JSON.stringify({
            type: COSELLER_MIC_FRAME.clockPong,
            id: frame.id,
            server_ms: frame.server_ms,
            client_ms: this.clientNowMs(),
          })
        );
        return;
      case COSELLER_MIC_FRAME.transcript:
        this.addOwnLine(frame, target);
        return;
      case COSELLER_MIC_FRAME.error:
        console.warn('[CoSellerMic] Server error:', frame.message);
        return;
      default:
        return;
    }
  }

  /** The co-seller's own line, from their own mic (the session socket skips these). Finals only. */
  private addOwnLine(frame: MicServerFrame, target: CoSellerMicTarget): void {
    if (!frame.is_final || !frame.text?.trim()) return;
    store.dispatch(
      addTranscript({
        text: frame.text,
        speaker: frame.speaker === 'customer' ? 'customer' : 'salesperson',
        participantId: participantIdOf(frame.participantId) ?? target.participantId,
        speakerName: frame.speakerName || undefined,
        timestamp: frame.timestamp || new Date().toISOString(),
      })
    );
  }

  private handleBlock(samples: Float32Array): void {
    const { resampler, accumulator, clock } = this;
    if (!resampler || !accumulator || !clock) return;
    const nowMs = this.clientNowMs();
    const frames = accumulator.push(resampler.process(samples));
    clock.observe(accumulator.totalSamples, nowMs);
    if (clock.takeDiscontinuity()) this.hasCaptureGap = true;
    frames.forEach((frame) => this.sendFrame(frame, clock));
  }

  private sendFrame(frame: PcmFrame, clock: CaptureClock): void {
    const socket = this.ws;
    // Before the welcome, and while down, audio is dropped: never buffered.
    if (!socket || socket.readyState !== WebSocket.OPEN || !this.isWelcomed) return;

    const audioMs = this.bytesSent / PCM16_BYTES_PER_MS;
    const isStampDue =
      this.hasCaptureGap || this.lastStampAudioMs === null || audioMs - this.lastStampAudioMs >= CAPTURE_STAMP_INTERVAL_MS;
    const utcMs = clock.utcAtSample(frame.startSample);
    if (this.isClockSync && isStampDue && utcMs !== null) {
      socket.send(JSON.stringify({ type: COSELLER_MIC_FRAME.captureStamp, audio_ms: audioMs, utc_ms: utcMs }));
      this.lastStampAudioMs = audioMs;
      this.hasCaptureGap = false;
    }

    socket.send(frame.bytes);
    this.bytesSent += frame.bytes.byteLength;
  }

  private closeSocket(): void {
    const socket = this.ws;
    this.ws = null;
    socket?.close(1000, 'Co-seller left');
  }
}

export const coSellerMicService = new CoSellerMicService();
